import React, { FC, FormEvent, useContext, useState } from 'react';

import {
  getI18nData,
  useSiteMetadata,
  InputEmailValidator,
  useProductUrls,
  registerUser,
} from '@lucid/utils';
import { LucidTheme, mqMax, ComponentsType } from '@lucid/theme';

import Box from '../box/box';
import Heading from '../heading/heading';
import Input from './input';
import LayoutContext from '../context/layout-context';
import Link from '../link/link';
import Text from '../text/text';

interface EmailOnlyI18nNode {
  node: {
    childSrcJson: {
      mktg_email_only_registering: string;
      mktg_nav_sign_up_free: string;
      mktg_email_only_sign_up_with: string;
      mktg_ct_all_default_form_heading: string;
      mktg_privacy_form: string;
      mktg_email_only_enterprise_msg: string;
      mktg_abtest_hp_work_email: string;
      mktg_abtest_signuppage_email_error: string;
    },
    name: string;
  }
}

export interface EmailOnlyI18nDataProps {
  edges: EmailOnlyI18nNode[];
}

export interface EmailOnlyProps {
  buttonText?: string;
  checkForService?: boolean;
  isVisio?: boolean;
  orTextColor?: string;
  pageType?: string;
  placeholder?: string;
  returnUrl?: string;
  stackAt?: ComponentsType['Breakpoints'];
}

export interface EmailOnlyBaseProps extends EmailOnlyProps {
  i18nData: EmailOnlyI18nDataProps;
}

const EmailOnlyBase: FC<EmailOnlyBaseProps> = ({
  buttonText = '',
  checkForService = false,
  i18nData,
  isVisio = false,
  pageType = '',
  placeholder = '',
  returnUrl,
  stackAt = 'mobile'
}) => {
  const [email, setEmail] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const [shouldShowValidation, setShouldShowValidation] = useState(false);
  const [isValid, setIsValid] = useState(false);

  const { product } = useSiteMetadata();
  const {
    language: { i18nKey, urlLangPrefix },
    modal
  } = useContext(LayoutContext);

  const { pricingUrl, productUrl } = useProductUrls(urlLangPrefix);

  const i18n = getI18nData(i18nData, i18nKey);
  const [buttonCopy, setButtonCopy] = useState(
    buttonText || i18n('mktg-nav-sign-up-free')
  );
  const registering = i18n('mktg-email-only-registering');

  const DEFAULT_RETURN_URL = `/${urlLangPrefix}users/level/newUser`;
  returnUrl = returnUrl ?? DEFAULT_RETURN_URL;

  const handleChange = (newValue: string) => {
    setEmail(newValue);
  };

  const submitEmailOnly = async (e: FormEvent<HTMLFormElement>) => {
    const emailReg = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    const visioQueryParam = '?type=visio';
    const timeoutMs = 6000;
    const failedUrl = pricingUrl;

    setShouldShowValidation(true);
    e.preventDefault();

    if (!isValid) {
      return;
    }

    const emailOnlyData = {
      email,
      finalizeNow: true,
      seenTos: true,
      discoveryPage: false
    };

    if (pageType === 'discovery') {
      emailOnlyData.discoveryPage = true;
    }

    if (emailReg.test(email)) {
      setButtonCopy(registering);
      setIsDisabled(true);

      try {
        const {
          isEnterpriseUser,
          redirectUrl,
          response
        } = await registerUser({
          data: emailOnlyData,
          urlLangPrefix,
          productUrl,
          product,
          checkForService
        });

        if (response.status === 200) {
          if (checkForService) {
            if (!isEnterpriseUser) {
              // regular registration, forward to pricing page
              window.location.assign(
                `${productUrl}/${urlLangPrefix}users/level/newUser`
              );
            } else {
              // they are confirmed enterprise, show modal and redirect after delay
              modal?.openModal(
                <Box
                  css={(theme: LucidTheme) => ({
                    hyphens: 'auto',
                    paddingLeft: theme.space[5],
                    paddingRight: theme.space[5]
                  })}
                >
                  <Heading as="h3" css={{ marginTop: 0 }} variant="headlineL">
                    {i18n('mktg-ct-all-default-form-heading')}
                  </Heading>
                  <Text css={{ marginBottom: 0 }}>
                    {i18n('mktg-email-only-enterprise-msg')}
                  </Text>
                </Box>
              );

              setTimeout(() => {
                window.location.assign(redirectUrl);
              }, timeoutMs);
            }
          } else {
            let url = redirectUrl;
            const paramSeparator = /\?./.test(url) ? '&' : '?';
            if (returnUrl?.includes('documents')) {
              url = `${redirectUrl}${paramSeparator}return_url=${returnUrl}&canInterruptWithPayments=1`;
            } else if (isVisio) {
              url = `${redirectUrl}${visioQueryParam}`;
            }
            window.location.assign(url);
          }
        } else {
          const url = `${failedUrl}${isVisio ? visioQueryParam : ''}`;
          window.location.assign(url);
        }
      } catch (innerE) {
        console.error(innerE);
        window.location.assign(failedUrl);
      }
    }
  };

  const block = 'eo-form';

  const dynamicMediaQuery = mqMax[stackAt];

  return (
    <form
      noValidate
      className={`js-${block}`}
      css={(theme: LucidTheme) => ([
        {
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          marginBottom: theme.space[8],
          maxWidth: '100%'
        },
        i18nKey === 'pt' && { maxWidth: '615px' }
      ])}
      method="post"
      onSubmit={submitEmailOnly}
    >
      <fieldset
        css={{
          width: '100%',
          border: 'none',
          [mqMax.mobile]: {
            flex: '0 1 100%',
            maxWidth: '100%',
            order: 0,
            marginBottom: 0,
            marginRight: 0,
            flexWrap: 'wrap',
          }
        }}
        disabled={isDisabled}
      >
        <Box
          css={[
            {
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              [dynamicMediaQuery]: {
                flex: '0 1 100%',
                maxWidth: '100%',
                flexWrap: 'wrap'
              }
            }
          ]}
        >
          <Box
            css={(theme: LucidTheme) => ([
              {
                flex: '0 1 49%',
                maxWidth: '49%',
                order: 1,
                marginBottom: theme.space[3],
                height: theme.space[7],
                [dynamicMediaQuery]: {
                  flex: '0 1 100%',
                  maxWidth: '100%',
                  order: 0,
                  marginBottom: theme.space[3]
                }
              },
              isDisabled && { opacity: 0.5, pointerEvents: 'none' },
            ])}
          >
            <Input
              hasFloatingLabel
              errorMessage={i18n('mktg-abtest-signuppage-email-error')}
              label={placeholder || i18n('mktg-abtest-hp-work-email')}
              name="email"
              onChange={handleChange}
              onValidate={passedValidation => setIsValid(passedValidation)}
              placeholder={placeholder || i18n('mktg-abtest-hp-work-email')}
              showValidation={shouldShowValidation}
              type="email"
              validator={InputEmailValidator}
              value={email}
            />
          </Box>
          <Link
            asButton
            primary
            css={(theme: LucidTheme) => ([
              {
                flex: '0 1 49%',
                maxWidth: '49%',
                order: 2,
                WebkitFontSmoothing: 'antialiased',
                marginTop: theme.space[0],
                marginBottom: theme.space[3],
                position: 'relative',
                '&:focus': {
                  outline: 'none'
                },
                [dynamicMediaQuery]: {
                  flex: '0 1 100%',
                  maxWidth: '100%',
                  order: 0,
                  marginRight: 0
                }
              },
              isDisabled && { opacity: 0.5 },
            ])}
            name="submit"
            size="large"
            type="submit"
          >
            {buttonCopy}
          </Link>
          <Text
            css={(theme: LucidTheme) => ([
              {
                flex: '0 1 49%',
                maxWidth: '100%',
                order: 3,
                color: theme.colors.coolGrey[30],
                WebkitFontSmoothing: 'antialiased',
                margin: 0,
                textAlign: 'left',
                '&:focus': {
                  outline: 'none'
                },
                [dynamicMediaQuery]: {
                  flex: '0 1 100%'
                }
              },
              {
                [`.${block}__link`]: {
                  textDecoration: 'underline',
                  color: theme.colors.coolGrey[30],
                  '&:hover': {
                    color: theme.colors.coolGrey[30]
                  },
                  '&:visited': {
                    color: theme.colors.coolGrey[30]
                  }
                }
              }
            ])}
            dangerouslySetInnerHTML={{
              __html: i18n('mktg-privacy-form', {}, [
                `<a href="https://lucid.co/tos" class="${block}__link">{}</a>`,
                `<a href="https://lucid.co/privacy" class="${block}__link">{}</a>`
              ])
            }}
            variant={'interactiveXS500'}
          />
        </Box>
      </fieldset>
    </form>
  );
};

export default EmailOnlyBase;
export { EmailOnlyBase };
